import './polyfill-process';
import { createSiteHeader } from './components/layout/SiteHeader';
import { createSiteFooter } from './components/layout/SiteFooter';
import { ROUTES } from './lib/routes';
import { register, startRouter } from './router';
import { renderHomePage } from './pages/HomePage';
import { renderNewInstallPage } from './pages/NewInstallPage';
import { renderConsultPage } from './pages/ConsultPage';
import { renderDifferentiatedServicesPage } from './pages/DifferentiatedServicesPage';
import { renderInstallCaseBoardPage } from './pages/InstallCaseBoardPage';
import { renderResourcesPage } from './pages/ResourcesPage';
import { renderExistingInstallPage } from './pages/ExistingInstallPage';
import { renderEvostDemoPage } from './pages/EvostDemoPage';
import { renderServiceApplicationPage } from './pages/ServiceApplicationPage';
import { renderSignupPage } from './pages/SignupPage';
import { renderLoginPage } from './pages/LoginPage';
import { renderMyApplicationsPage } from './pages/MyApplicationsPage';
import { renderProfilePage } from './pages/ProfilePage';
import { renderManagePage } from './pages/manage/ManagePage';
import { renderManageResourcesPage } from './pages/manage/ManageResourcesPage';
import { renderManageInstallCasesPage } from './pages/manage/ManageInstallCasesPage';
import { clear, el } from './utils/dom';
import { initAnalyticsConsent, trackPageView } from './analytics';
import './styles/app.css';

const TITLES: Record<string, string> = {
  [ROUTES.home]: 'EV123 — EV 충전 설치 계획 포털',
  [ROUTES.homePublic]: 'EV123 — EV 충전 설치 계획 포털',
  [ROUTES.newInstall]: '신규설치 기준 계산 — EV123',
  [ROUTES.consult]: '상담 신청 — EV123',
  [ROUTES.differentiatedServices]: '차별화 서비스 — EV123',
  [ROUTES.installCaseBoard]: '설치 사례 — EV123',
  [ROUTES.resources]: '자료실 — EV123',
  [ROUTES.existing]: '기설치 이력 조회 — EV123',
  [ROUTES.evostDemo]: 'EVOS-T 시설 화면 — EV123',
  [ROUTES.serviceApply]: 'EV 서비스 도입 신청 — EV123',
  [ROUTES.signup]: '회원 가입 신청 — EV123',
  [ROUTES.login]: '로그인 — EV123',
  [ROUTES.myApplications]: '신청내역 조회 — EV123',
  [ROUTES.myProfile]: '내 프로필 — EV123',
  [ROUTES.manage]: '관리자 대시보드 — EV123',
  [ROUTES.manageResources]: '자료실 관리 — EV123',
  [ROUTES.manageInstallCases]: '설치사례 관리 — EV123',
};

function main(): void {
  const app = document.querySelector<HTMLDivElement>('#app');
  if (!app) return;

  const shell = el('div', { className: 'ev123-shell' });
  const skip = el('a', {
    className: 'ev123-skip',
    href: '#ev123-main',
    text: '본문으로 건너뛰기',
  });
  shell.appendChild(skip);

  const headerApi = createSiteHeader();
  shell.appendChild(headerApi.element);
  document.body.appendChild(headerApi.drawerElement);

  const main = el('main', { id: 'ev123-main', className: 'ev123-main' });
  shell.appendChild(main);

  const footer = createSiteFooter();
  shell.appendChild(footer);

  app.appendChild(shell);
  initAnalyticsConsent();

  const renderFor = (path: string): void => {
    const p = path === '' ? ROUTES.home : path;
    document.title = TITLES[p] ?? TITLES[ROUTES.home];
    headerApi.setActivePath(p);
    const mainEl = main as HTMLElement & { __ev123PageLeave?: () => void };
    mainEl.__ev123PageLeave?.();
    mainEl.__ev123PageLeave = undefined;
    clear(main);

    switch (p) {
      case ROUTES.newInstall:
        renderNewInstallPage(main);
        break;
      case ROUTES.consult:
        renderConsultPage(main);
        break;
      case ROUTES.differentiatedServices:
        renderDifferentiatedServicesPage(main);
        break;
      case ROUTES.installCaseBoard:
        renderInstallCaseBoardPage(main);
        break;
      case ROUTES.resources:
        void renderResourcesPage(main);
        break;
      case ROUTES.existing:
        renderExistingInstallPage(main);
        break;
      case ROUTES.evostDemo:
        renderEvostDemoPage(main);
        break;
      case ROUTES.serviceApply:
        renderServiceApplicationPage(main);
        break;
      case ROUTES.signup:
        renderSignupPage(main);
        break;
      case ROUTES.login:
        renderLoginPage(main);
        break;
      case ROUTES.myApplications:
        renderMyApplicationsPage(main);
        break;
      case ROUTES.myProfile:
        renderProfilePage(main);
        break;
      case ROUTES.manage:
        renderManagePage(main);
        break;
      case ROUTES.manageResources:
        renderManageResourcesPage(main);
        break;
      case ROUTES.manageInstallCases:
        renderManageInstallCasesPage(main);
        break;
      case ROUTES.home:
      default:
        renderHomePage(main);
        break;
    }
    trackPageView();
  };

  register(ROUTES.home, () => renderFor(ROUTES.home));
  register(ROUTES.newInstall, () => renderFor(ROUTES.newInstall));
  register(ROUTES.consult, () => renderFor(ROUTES.consult));
  register(ROUTES.differentiatedServices, () => renderFor(ROUTES.differentiatedServices));
  register(ROUTES.installCaseBoard, () => renderFor(ROUTES.installCaseBoard));
  register(ROUTES.resources, () => renderFor(ROUTES.resources));
  register(ROUTES.existing, () => renderFor(ROUTES.existing));
  register(ROUTES.evostDemo, () => renderFor(ROUTES.evostDemo));
  register(ROUTES.serviceApply, () => renderFor(ROUTES.serviceApply));
  register(ROUTES.signup, () => renderFor(ROUTES.signup));
  register(ROUTES.login, () => renderFor(ROUTES.login));
  register(ROUTES.myApplications, () => renderFor(ROUTES.myApplications));
  register(ROUTES.myProfile, () => renderFor(ROUTES.myProfile));
  register(ROUTES.manage, () => renderFor(ROUTES.manage));
  register(ROUTES.manageResources, () => renderFor(ROUTES.manageResources));
  register(ROUTES.manageInstallCases, () => renderFor(ROUTES.manageInstallCases));

  startRouter();
}

main();
